import { useMutation } from "@apollo/client/react";
import { DSTextarea } from "@components/design-system/molecules";
import { DSModal } from "@components/design-system/organisms/DSModal";
import { faPaw } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import ADD_FEEDBACK_TO_CUSTOMIZATION from "@graphql/mutations/AddFeedbackToCustomization";
import React, { useState } from "react";

import { alert } from "../../../common/Alert";

const CustomizationFeedback = ({ llmCustomizationUuid }) => {
  //state
  const [submittedResult, setSubmittedResult] = useState(null);
  const [additionalFeedback, setAdditionalFeedback] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [addFeedbackToCustomization] = useMutation(
    ADD_FEEDBACK_TO_CUSTOMIZATION,
  );

  //handlers
  const handleSubmit = (result) => {
    setSubmittedResult(result);
    const message =
      result === "good"
        ? "Glad to hear that! We'll use this feedback to improve your future notes!"
        : "Sorry to hear that! We'll use this feedback to improve your future notes!";
    alert(result === "good" ? "success" : "warning", message);
    setShowModal(true);
  };

  const handleFeedbackSubmission = async (
    includeAdditionalFeedback = false,
  ) => {
    const addFeedbackInput = {
      llmCustomizationUuid,
      rating: submittedResult,
      feedback: includeAdditionalFeedback
        ? additionalFeedback
        : undefined,
    };

    try {
      await addFeedbackToCustomization({
        variables: { addFeedbackInput },
      });
      alert("success", "Feedback submitted successfully.");
    } catch {
      alert("error", "Failed to submit feedback. Please try again.");
    } finally {
      setShowModal(false);
    }
  };

  return (
    <>
      <div className="flex justify-between items-center my-2 px-2">
        <p className="text-sm font-bold">
          What did you think of this result?
        </p>
        <div className="flex space-x-1">
          <button
            onClick={() => handleSubmit("good")}
            data-testid="thumbs-up-button"
            className={`${
              submittedResult === "good"
                ? "bg-green-400 text-white"
                : "text-primary hover:text-white bg-green-200 dark:bg-green-700 hover:bg-green-400 dark:hover:bg-green-600"
            } rounded-full h-7 w-7 shadow-xs transition-colors`}
          >
            <FontAwesomeIcon icon={faPaw} />
          </button>
          <button
            onClick={() => handleSubmit("bad")}
            data-testid="thumbs-down-button"
            className={`${
              submittedResult === "bad"
                ? "bg-red-400 text-white"
                : "text-primary hover:text-white bg-red-200 dark:bg-red-700 hover:bg-red-400 dark:hover:bg-red-600"
            } rounded-full h-7 w-7 shadow-xs transition-colors`}
          >
            <FontAwesomeIcon
              icon={faPaw}
              className="transform -rotate-180"
            />
          </button>
        </div>
      </div>
      <DSModal
        isOpen={showModal}
        onClose={() => handleFeedbackSubmission(false)}
        title="Additional Feedback"
        actions={[
          {
            label: "Skip",
            variant: "cancel",
            onClick: () => handleFeedbackSubmission(false),
          },
          {
            label: "Submit",
            variant: "primary",
            onClick: () => handleFeedbackSubmission(true),
            dataTestId: "confirm-button",
          },
        ]}
        dataTestId="customization-feedback-modal"
      >
        <DSTextarea
          minRows={3}
          placeholder="Let us know how we can improve (optional)"
          value={additionalFeedback}
          onChange={(e) => setAdditionalFeedback(e.target.value)}
          dataTestId="feedback-textarea"
        />
      </DSModal>
    </>
  );
};

export default CustomizationFeedback;
